'use client';

import { motion } from 'framer-motion';
import { Users, Package, Building2, TrendingUp } from 'lucide-react';

const EASE = [0.16, 1, 0.3, 1] as const;

// ─── Data ─────────────────────────────────────────────────────────────────────
// One unified dashboard: three breakdowns of the same business, stacked.
// Order is Product → Client → Employee (per request).

type Row = { name: string; meta: string; profit: string; margin: string };

type Section = {
  key: string;
  icon: typeof Users;
  label: string;
  rows: Row[];
  footer: string;
};

const SECTIONS: Section[] = [
  {
    key: 'product',
    icon: Package,
    label: 'By Product',
    rows: [
      { name: 'Core Platform',    meta: 'SaaS', profit: '+$640K', margin: '52%' },
      { name: 'Analytics Add-on', meta: 'SaaS', profit: '+$310K', margin: '47%' },
    ],
    footer: '+ 15 more products',
  },
  {
    key: 'client',
    icon: Building2,
    label: 'By Client',
    rows: [
      { name: 'Acme Corp', meta: 'Enterprise', profit: '+$284K', margin: '44%' },
      { name: 'Northwind', meta: 'Enterprise', profit: '+$196K', margin: '39%' },
    ],
    footer: '+ 87 more clients',
  },
  {
    key: 'employee',
    icon: Users,
    label: 'By Employee',
    rows: [
      { name: 'Sarah Chen', meta: 'Engineering', profit: '+$142K', margin: '38%' },
      { name: 'Alex Park',  meta: 'Sales',       profit: '+$98K',  margin: '42%' },
    ],
    footer: '+ 51 more people',
  },
];

const KPIS = [
  { label: 'Revenue TTM',     value: '$8.43M', delta: '+12.4%' },
  { label: 'Avg margin',      value: '38.4%' },
  { label: 'Sources synced',  value: '∞' },
];

// ═════════════════════════════════════════════════════════════════════════════

export default function HeroProductPanel() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, ease: EASE, delay: 0.3 }}
      className="relative w-full max-w-[820px] flex items-center"
    >
      {/* Soft single halo */}
      <div
        aria-hidden
        className="pointer-events-none absolute -inset-8 -z-10 opacity-70"
        style={{ background: 'radial-gradient(50% 50% at 50% 40%, rgba(167,139,250,0.28), transparent 70%)' }}
      />

      {/* Card — sizes to content instead of stretching to fill column */}
      <div className="relative flex flex-col w-full rounded-2xl border border-purple-100/80 bg-white shadow-[0_40px_100px_-30px_rgba(76,29,149,0.30)] overflow-hidden">
        {/* Top accent hairline */}
        <div aria-hidden className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, #A78BFA, transparent)' }} />

        {/* Window chrome */}
        <div className="flex items-center justify-between px-5 py-2.5 border-b border-gray-100">
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-gray-200" />
            <span className="h-2 w-2 rounded-full bg-gray-200" />
            <span className="h-2 w-2 rounded-full bg-gray-200" />
          </div>
          <span className="text-[11px] tracking-wider text-gray-400 font-medium">blueberries.app</span>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-emerald-700">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
            </span>
            Live
          </span>
        </div>

        {/* Body */}
        <div className="flex flex-col px-5 pt-3.5 pb-3.5">
          {/* Section header */}
          <div className="flex items-baseline justify-between">
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-purple-700/80 font-semibold">Real-time profitability</div>
              <div className="mt-1 text-[15px] font-semibold text-gray-900">Last quarter · trailing</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Updated</div>
              <div className="text-[11px] text-gray-700 font-medium">2s ago</div>
            </div>
          </div>

          {/* KPI strip — top-level company metrics */}
          <div className="mt-3 grid grid-cols-3 gap-2">
            {KPIS.map((k, i) => (
              <motion.div
                key={k.label}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.35 + i * 0.05, ease: EASE }}
                className="rounded-xl border border-gray-100 bg-gradient-to-b from-white to-gray-50/40 px-3 py-2"
              >
                <div className="text-[9.5px] tracking-[0.18em] uppercase text-gray-400 font-semibold">{k.label}</div>
                <div className="mt-1 flex flex-col items-start gap-0.5 md:flex-row md:items-baseline md:gap-1.5">
                  <span className="text-[15px] md:text-[17px] font-semibold text-gray-900 tabular-nums leading-none whitespace-nowrap">{k.value}</span>
                  {k.delta && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-emerald-600 tabular-nums whitespace-nowrap">
                      <TrendingUp className="h-2.5 w-2.5" strokeWidth={2.5} />
                      {k.delta}
                    </span>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Three breakdown sections — all visible, ordered Product → Client → Employee */}
          <div className="mt-3 space-y-2.5">
            {SECTIONS.map((sec, idx) => (
              <BreakdownSection key={sec.key} section={sec} delay={0.5 + idx * 0.1} />
            ))}
          </div>

          {/* Total summary */}
          <div className="mt-3 grid grid-cols-[1.4fr_auto] gap-3 items-end rounded-xl border border-purple-100 bg-gradient-to-br from-purple-50/70 via-white to-purple-50/40 px-4 py-2.5">
            <div>
              <div className="text-[9.5px] tracking-[0.22em] uppercase text-purple-700/80 font-semibold">Total</div>
              <div className="mt-1 text-[10.5px] text-gray-600 font-medium leading-tight">Net profit · last quarter</div>
            </div>
            <div className="text-[24px] md:text-[26px] font-semibold tabular-nums leading-none bg-gradient-to-br from-[#4C1D95] via-[#6D28D9] to-[#A78BFA] bg-clip-text text-transparent">
              +$2.41M
            </div>
          </div>

          {/* Data sources line */}
          <div className="mt-2.5 flex items-center justify-between px-1 text-[10.5px] text-gray-400 font-medium">
            <span className="inline-flex items-center gap-1.5 text-purple-700">
              <span className="h-1 w-1 rounded-full bg-purple-500" />
              Financial · Operational · Comms data
            </span>
            <span className="tracking-wider">∞ sources supported</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ─── Breakdown section ───────────────────────────────────────────────────────

function BreakdownSection({ section, delay }: { section: Section; delay: number }) {
  const Icon = section.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay, ease: EASE }}
    >
      {/* Section header */}
      <div className="flex items-center gap-2 px-1 mb-1">
        <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-purple-50 ring-1 ring-purple-100">
          <Icon className="h-3 w-3 text-purple-700" strokeWidth={2.2} />
        </span>
        <span className="text-[11.5px] font-semibold text-gray-900 tracking-tight">{section.label}</span>
        <span className="ml-auto text-[10px] text-gray-400 font-medium">{section.footer}</span>
      </div>

      {/* Rows */}
      <div className="rounded-xl border border-gray-100 bg-gradient-to-b from-white to-gray-50/30 overflow-hidden divide-y divide-gray-50">
        {section.rows.map((r) => (
          <div
            key={r.name}
            className="grid grid-cols-[1.5fr_0.85fr_0.65fr] gap-3 items-center px-3 py-1 hover:bg-gray-50/80 transition-colors"
          >
            <div className="min-w-0">
              <div className="text-[13px] font-semibold text-gray-900 truncate leading-tight">{r.name}</div>
              <div className="text-[10.5px] text-gray-500 leading-tight mt-0.5">{r.meta}</div>
            </div>
            <div className="text-[13.5px] font-semibold tabular-nums text-emerald-600">{r.profit}</div>
            <div className="text-[12.5px] font-medium tabular-nums text-gray-700 text-right">{r.margin}</div>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

